import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostBinding, ViewChild, computed, effect, input, output, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CarouselComponent, CarouselModule, OwlOptions, SlidesOutputData } from 'ngx-owl-carousel-o';

import { ColorVariant } from '../product-list-card/product-list-card.component';

@Component({
  standalone: true,
  selector: 'base-variant-viewer',
  templateUrl: './variant-viewer.component.html',
  styleUrls: ['./variant-viewer.component.scss'],
  imports: [CommonModule, RouterModule, CarouselModule]
})
export class VariantViewerComponent {

  @ViewChild(CarouselComponent) carouselComponent!: CarouselComponent;
  @ViewChild('scrollAbleContainer', { static: false }) scrollAbleContainer!: ElementRef<HTMLElement>;

  // Angular 20: Convert @Input() to input()
  readonly variants = input<ColorVariant[]>([]);
  readonly showImages = input<boolean>(false);

  // Angular 20: Convert @Output() to output()
  mouseEnterIndex = output<string>();
  mouseLeaveIndex = output<string>();
  showImages$ = output<boolean>();

  // Signals for internal state
  private _isDragging = signal<boolean>(false);
  private _startPosition = signal<number>(0);

  readonly isDraggingSignal = this._isDragging.asReadonly();
  readonly startPositionSignal = this._startPosition.asReadonly();
  readonly hasVariants = computed(() => this.variants().length > 0);
  readonly shouldAutoWidth = computed(() => this.variants().length <= 20);
  readonly showNavigationButtons = computed(() => this.variants().length > 4);

  // Setter methods for template binding
  set isDragging(value: boolean) {
    this._isDragging.set(value);
  }

  customOptions: OwlOptions = {
    items: 4,
    loop: false,
    margin: 0,
    autoplay: false,
    dots: false,
    responsive: {
      0: {
        items: 4
      },
      600: {
        items: 4
      },
      900: {
        items: 4
      }
    },
    nav: false
  };

  @HostBinding('style.--card-width')
  get cardWidth(): string {
    const noOfCards = 4;
    return `calc((100% - (5px * ${noOfCards - 1})) / ${noOfCards})`;
  }

  constructor() {
    // Angular 20: Convert ngOnChanges to effect()
    // Reset startPosition when showImages changes
    effect(() => {
      const showImages = this.showImages();
      if (showImages) {
        this._startPosition.set(0);
      }
    });

    // Update carousel options when variants change
    effect(() => {
      const variants = this.variants();
      if (variants.length <= 20) {
        this.customOptions.autoWidth = true;
        this.customOptions.margin = 5;
      }
    });
  }

  onShowImages() {
    this.showImages$.emit(true);
  }

  translated(slidesOutputData: SlidesOutputData) {
    this._startPosition.set(slidesOutputData.startPosition ?? 0);
  }

  mouseEnter(imageUrl: string) {
    this.mouseEnterIndex.emit(imageUrl);
  }

  mouseLeave(imageUrl: string) {
    this.mouseLeaveIndex.emit(imageUrl);
  }

  navigationLeft(): void {
    try {
      if (!this.scrollAbleContainer?.nativeElement) {
        return;
      }

      const container = this.scrollAbleContainer.nativeElement;
      const cardWidth = (container.children[0] as HTMLElement).offsetWidth;
      const currentScrollLeft = container.scrollLeft;

      container.scrollTo({
        left: currentScrollLeft - cardWidth,
        behavior: 'smooth',
      });
    } catch (error) {
      console.error('Error in navigationLeft():', error);
    }
  }

  navigationRight(): void {
    try {
      if (!this.scrollAbleContainer?.nativeElement) {
        return;
      }

      const container = this.scrollAbleContainer.nativeElement;
      const cardWidth = (container.children[0] as HTMLElement).offsetWidth;
      const currentScrollLeft = container.scrollLeft;

      container.scrollTo({
        left: currentScrollLeft + cardWidth,
        behavior: 'smooth',
      });
    } catch (error) {
      console.error('Error in navigationRight():', error);
    }
  }
}
